import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '../../../../../../../ui/toast';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../../i18n/constants/keys';
import { Button, NameEmailAvatar } from '../../../../../../../ui';
import { DIALOG_TYPE } from '../../../../../Constants/DialogTypes';
import { IOfferDetails, IUser } from '../Interfaces';

interface ICandidateDetails {
  selectedCandidate: IOfferDetails;
  isUpdatingStatus: boolean;
  updateCandidateOfferStatus: (candidateId: string, status: string) => void;
}
export const CandidateDetails = (props: ICandidateDetails) => {
  const { selectedCandidate, isUpdatingStatus, updateCandidateOfferStatus } = props;
  const { t } = useTranslation(NS.adminPages);
  const CD = ADMIN_PAGES_KEYS.jobOpenings.smartInterviews.offer.candidateDetails;
  const [isConfirmed, setIsConfirmed] = useState(false);

  // Get user details directly from the new API response structure with defensive checks
  const userDetails = selectedCandidate?.interviewApplicant?.user || {
    firstName: '',
    lastName: '',
    email: '',
  };
  const userName = `${userDetails.firstName || ''} ${userDetails.lastName || ''}`.trim() || t(CD.unknown);

  // Get current status for rendering the appropriate panel
  const status = selectedCandidate?.applicant_status || '';
  return (
    <section className='col-span-full bg-white rounded-3xl shadow-lg border border-gray-200 overflow-hidden'>
      <div className='p-4 border-b border-gray-200 bg-gray-50'>
        <h2 className='text-lg font-semibold text-gray-800'>
          { t(CD.title) }
        </h2>
      </div>

      <div className='p-6 space-y-6'>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          { /* Left column */ }
          <div className='space-y-4'>
            { /* User details with proper avatar component */ }
            <div className='mb-6'>
              { userDetails && (
                <NameEmailAvatar
                    name={`${userDetails.firstName || ''} ${
                      userDetails.lastName || ''
                    }`}
                    email={userDetails.email || ''}
                    avatar={''}
                    avatarWidth='48px'
                    avatarHeight='48px'
                    nameFontSize='1.1rem'
                    emailFontSize='0.9rem'
                    applicantStatus={selectedCandidate.applicant_status}
                />
              ) }
            </div>

            <div>
              <h3 className='text-gray-500 text-sm'>{ t(CD.status) }</h3>
              <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    selectedCandidate.applicant_status &&
                  selectedCandidate.applicant_status.includes('Accepted')
                      ? 'bg-green-100 text-green-800'
                      : selectedCandidate.applicant_status &&
                      selectedCandidate.applicant_status.includes('Rejected')
                        ? 'bg-red-100 text-red-800'
                        : selectedCandidate.applicant_status &&
                      selectedCandidate.applicant_status.includes('Withheld')
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-blue-100 text-blue-800'
                  }`}
              >
                { selectedCandidate.applicant_status || 'Unknown' }
              </span>
            </div>

            <div>
              <h3 className='text-gray-500 text-sm'>{ t(CD.interviewType) }</h3>
              <p>
                { selectedCandidate?.interviewApplicant?.interviewType || 'N/A' }
              </p>
            </div>

            <div>
              <h3 className='text-gray-500 text-sm'>{ t(CD.interviewDate) }</h3>
              <p>
                { selectedCandidate?.interviewApplicant?.interviewStartTime
                  ? new Date(
                    selectedCandidate.interviewApplicant.interviewStartTime *
                        1000,
                  ).toLocaleDateString()
                  : 'N/A' }
              </p>
            </div>
          </div>

          { /* Right column */ }
          <div className='space-y-4'>
            <div>
              <h3 className='text-gray-500 text-sm'>{ t(CD.averageScore) }</h3>
              <div className='flex items-center'>
                <div className='w-full bg-gray-200 rounded-full h-2.5'>
                  <div
                      className={`h-2.5 rounded-full ${
                        (selectedCandidate?.interviewApplicant?.averageScore ||
                        0) >= 70
                          ? 'bg-green-500'
                          : (selectedCandidate?.interviewApplicant
                            ?.averageScore || 0) >= 50
                            ? 'bg-yellow-500'
                            : 'bg-red-500'
                      }`}
                      style={{
                        width: `${
                          selectedCandidate?.interviewApplicant?.averageScore || 0
                        }%`,
                      }}
                  ></div>
                </div>
                <span className='ml-2 text-sm font-medium text-gray-700'>
                  { selectedCandidate?.interviewApplicant?.averageScore || 0 }%
                </span>
              </div>
            </div>

            <div>
              <h3 className='text-gray-500 text-sm'>{ t(CD.aiScore) }</h3>
              <div className='flex items-center'>
                <div className='w-full bg-gray-200 rounded-full h-2.5'>
                  <div
                      className={`h-2.5 rounded-full ${
                        (selectedCandidate?.interviewApplicant?.aiOverallScore
                          ?.score || 0) >= 70
                          ? 'bg-green-500'
                          : (selectedCandidate?.interviewApplicant?.aiOverallScore
                            ?.score || 0) >= 50
                            ? 'bg-yellow-500'
                            : 'bg-red-500'
                      }`}
                      style={{
                        width: `${
                          selectedCandidate?.interviewApplicant?.aiOverallScore
                            ?.score || 0
                        }%`,
                      }}
                  ></div>
                </div>
                <span className='ml-2 text-sm font-medium text-gray-700'>
                  { selectedCandidate?.interviewApplicant?.aiOverallScore
                    ?.score || 0 }
                  %
                </span>
              </div>
            </div>

            <div>
              <h3 className='text-gray-500 text-sm'>{ t(CD.location) }</h3>
              <p>
                { selectedCandidate?.interviewApplicant?.inPersonAddress ||
                  'Virtual' }
              </p>
            </div>

            <div>
              <h3 className='text-gray-500 text-sm'>{ t(CD.meetingLink) }</h3>
              <p className='truncate'>
                { selectedCandidate?.interviewApplicant?.meetingLink || 'N/A' }
              </p>
            </div>
          </div>
        </div>

        { /* Skills section */ }
        <div className='pt-4 border-t border-gray-200'>
          <h3 className='font-medium text-gray-900 mb-3'>{ t(CD.skillsAssessment) }</h3>
          <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
            { ([
              { key: t(CD.skills.technicalSkills), data: selectedCandidate?.interviewApplicant?.technicalSkills },
              { key: t(CD.skills.roleExperience), data: selectedCandidate?.interviewApplicant?.roleExperience },
              { key: t(CD.skills.industrySkills), data: selectedCandidate?.interviewApplicant?.industrySkills },
              { key: t(CD.skills.productKnowledge), data: selectedCandidate?.interviewApplicant?.productKnowledge },
              { key: t(CD.skills.communication), data: selectedCandidate?.interviewApplicant?.communication },
              { key: t(CD.skills.problemSolving), data: selectedCandidate?.interviewApplicant?.problemSolving },
              { key: t(CD.skills.leadership), data: selectedCandidate?.interviewApplicant?.leadership },
              { key: t(CD.skills.teamCollaboration), data: selectedCandidate?.interviewApplicant?.teamCollaboration },
            ] as Array<{ key: string; data: any }>).map(({ key: skillName, data: skillData }) => (
              <div key={skillName} className='bg-gray-50 p-3 rounded'>
                <h4 className='text-xs text-gray-500'>{ skillName }</h4>
                <div className='flex items-center mt-1'>
                  <div className='w-full bg-gray-200 rounded-full h-1.5'>
                    <div
                        className={`h-1.5 rounded-full ${
                          (skillData?.score || 0) >= 70
                            ? 'bg-green-500'
                            : (skillData?.score || 0) >= 50
                              ? 'bg-yellow-500'
                              : 'bg-red-500'
                        }`}
                        style={{ width: `${skillData?.score || 0}%` }}
                    ></div>
                  </div>
                  <span className='ml-2 text-xs font-medium text-gray-700'>
                    { skillData?.score || 0 }
                  </span>
                </div>
              </div>
            )) }
          </div>
        </div>

        { /* Actions section */ }
        <div className='pt-4 border-t border-gray-200'>
          { /* Enhanced Offer Management UI */ }
          { (() => {
            // For statuses that need decision making
            if (
              status === 'Interview' ||
              !status ||
              status === 'Offer' // Default status when first adding to offer stage
            ) {
              return (
                <div className='bg-rose-50 bg-opacity-90 backdrop-blur-sm rounded-3xl w-full shadow-lg p-8 space-y-8'>
                  <div className='space-y-2'>
                    <h2 className='text-2xl font-semibold text-gray-900 select-none'>
                      { t(CD.makeFinalDecision) }
                    </h2>
                    <p className='text-sm text-gray-600 max-w-xl'>
                      { t(CD.finalDecisionSubtitle) }{ ' ' }
                      <span className='font-medium text-rose-600'>
                        { userDetails?.firstName || t(CD.unknown) }
                      </span>
                    </p>
                  </div>

                  { /* Confirmation Checkbox */ }
                  <label className='flex items-start gap-4 bg-white bg-opacity-70 border border-gray-300 rounded-2xl p-5 cursor-pointer transition hover:bg-white'>
                    <input
                        type='checkbox'
                        className='mt-1 accent-rose-500 w-5 h-5 rounded-md focus:ring-2 focus:ring-rose-400'
                        checked={isConfirmed}
                        onChange={(e) => setIsConfirmed(e.target.checked)}
                        aria-checked={isConfirmed}
                    />
                    <span className='text-sm text-gray-800 leading-relaxed'>
                      { t(CD.confirmCheckbox) }
                    </span>
                  </label>

                  { /* Action Buttons */ }
                  <div className='flex flex-col sm:flex-row gap-5 justify-end'>
                    <button
                        onClick={() => {
                          if (!isConfirmed) {
                            toast.warning(t(CD.pleaseConfirmFirst), {
                              position: 'bottom-center',
                            });
                            return;
                          }
                          updateCandidateOfferStatus(
                            selectedCandidate.jobApplicantId,
                            'Offer-Declined',
                          );
                        }}
                        className='w-full sm:w-auto px-6 py-3 text-sm font-semibold rounded-2xl bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-md'
                        disabled={!isConfirmed || isUpdatingStatus}
                    >
                      { t(CD.rejectCandidate) }
                    </button>
                    <button
                        onClick={() => {
                          if (!isConfirmed) {
                            toast.warning(t(CD.pleaseConfirmFirst), {
                              position: 'bottom-center',
                            });
                            return;
                          }
                          updateCandidateOfferStatus(
                            selectedCandidate.jobApplicantId,
                            'Offer-Extended',
                          );
                        }}
                        disabled={!isConfirmed || isUpdatingStatus}
                        className='w-full sm:w-auto px-6 py-3 text-sm font-semibold rounded-2xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-md'
                    >
                      { t(CD.sendOffer) }
                    </button>
                  </div>
                </div>
              );
            }

            // Status: Offer Extended - show status with option to withdraw
            if (status === 'Offer-Extended') {
              return (
                <div className='bg-emerald-50 bg-opacity-90 backdrop-blur-sm rounded-3xl w-full shadow-lg p-8 space-y-6'>
                  <div className='flex items-center gap-4'>
                    <div className='w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center'>
                      <svg
                          className='w-6 h-6 text-emerald-600'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                      >
                        <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M5 13l4 4L19 7'
                        />
                      </svg>
                    </div>
                    <div>
                      <h2 className='text-2xl font-semibold text-gray-900'>
                        { t(CD.offerExtended) }
                      </h2>
                      <p className='text-sm text-gray-600'>
                        { t(CD.offerSentTo) }{ ' ' }
                        <span className='font-medium text-emerald-600'>
                          { userDetails?.firstName || t(CD.unknown) }
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className='bg-white bg-opacity-70 border border-emerald-200 rounded-2xl p-5'>
                    <p className='text-sm text-gray-700'>
                      { t(CD.candidateReviewing) }
                    </p>
                  </div>
                  { /* Option to withdraw offer if needed */ }
                  <div className='flex flex-col sm:flex-row gap-5 justify-end'>
                    <button
                        onClick={() => {
                          updateCandidateOfferStatus(
                            selectedCandidate.jobApplicantId,
                            'Offer-Declined',
                          );
                        }}
                        disabled={isUpdatingStatus}
                        className='w-full sm:w-auto px-6 py-3 text-sm font-semibold rounded-2xl bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-md'
                    >
                      { t(CD.withdrawOffer) }
                    </button>
                    <button
                        onClick={() => {
                          updateCandidateOfferStatus(
                            selectedCandidate.jobApplicantId,
                            'Offer-Accepted',
                          );
                        }}
                        disabled={isUpdatingStatus}
                        className='px-6 py-3 text-sm font-semibold rounded-2xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-md'
                    >
                      { t(CD.candidateAcceptedOffer) }
                    </button>
                  </div>
                </div>
              );
            }

            // Status: Offer Accepted - show congratulations
            if (status === 'Offer-Accepted') {
              return (
                <div className='bg-emerald-50 bg-opacity-90 backdrop-blur-sm rounded-3xl w-full shadow-lg p-8 space-y-6'>
                  <div className='flex items-center gap-4'>
                    <div className='w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center'>
                      <svg
                          className='w-6 h-6 text-emerald-600'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                      >
                        <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
                        />
                      </svg>
                    </div>
                    <div>
                      <h2 className='text-2xl font-semibold text-gray-900'>
                        { t(CD.offerAccepted) }
                      </h2>
                      <p className='text-sm text-gray-600'>
                        <span className='font-medium text-emerald-600'>
                          { userDetails?.firstName || t(CD.unknown) }
                        </span>{ ' ' }
                        { t(CD.hasAccepted) }
                      </p>
                    </div>
                  </div>

                  <div className='bg-white bg-opacity-70 border border-emerald-200 rounded-2xl p-5'>
                    <p className='text-sm text-gray-700'>
                      { t(CD.congratulations) }
                    </p>
                  </div>
                </div>
              );
            }

            // Status: Offer Declined - show with option to extend new offer
            if (status === 'Offer-Declined') {
              return (
                <div className='bg-orange-50 bg-opacity-90 backdrop-blur-sm rounded-3xl w-full shadow-lg p-8 space-y-6'>
                  <div className='flex items-center gap-4'>
                    <div className='w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center'>
                      <svg
                          className='w-6 h-6 text-orange-600'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                      >
                        <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
                        />
                      </svg>
                    </div>
                    <div>
                      <h2 className='text-2xl font-semibold text-gray-900'>
                        { t(CD.offerRejectedDeclined) }
                      </h2>
                      <p className='text-sm text-gray-600'>
                        <span className='font-medium text-orange-600'>
                          { userDetails?.firstName || t(CD.unknown) }
                        </span>{ ' ' }
                        { status === 'Offer-Declined'
                          ? t(CD.wasRejected)
                          : t(CD.wasDeclined) }
                      </p>
                    </div>
                  </div>

                  <div className='bg-white bg-opacity-70 border border-orange-200 rounded-2xl p-5'>
                    <p className='text-sm text-gray-700'>
                      { status === 'Offer-Declined'
                        ? t(CD.notSelectedMessage)
                        : t(CD.declinedMessage) }
                    </p>
                  </div>

                  { status === 'Offer-Declined' && (
                    <div className='flex flex-col sm:flex-row gap-4 justify-end'>
                      <button
                          onClick={() => {
                            updateCandidateOfferStatus(
                              selectedCandidate.jobApplicantId,
                              'Offer-Extended',
                            );
                          }}
                          disabled={isUpdatingStatus}
                          className='px-6 py-3 text-sm font-semibold rounded-2xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-md'
                      >
                        { t(CD.extendRevisedOffer) }
                      </button>
                    </div>
                  ) }
                </div>
              );
            }

            // Status: Offer Withheld - show with option to extend offer
            if (status === 'Offer-Withheld') {
              return (
                <div className='bg-yellow-50 bg-opacity-90 backdrop-blur-sm rounded-3xl w-full shadow-lg p-8 space-y-6'>
                  <div className='flex items-center gap-4'>
                    <div className='w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center'>
                      <svg
                          className='w-6 h-6 text-yellow-600'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                      >
                        <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
                        />
                      </svg>
                    </div>
                    <div>
                      <h2 className='text-2xl font-semibold text-gray-900'>
                        { t(CD.offerWithheld) }
                      </h2>
                      <p className='text-sm text-gray-600'>
                        { t(CD.offerOnHold) }{ ' ' }
                        <span className='font-medium text-yellow-600'>
                          { userDetails?.firstName || t(CD.unknown) }
                        </span>
                      </p>
                    </div>
                  </div>

                  <div className='bg-white bg-opacity-70 border border-yellow-200 rounded-2xl p-5'>
                    <p className='text-sm text-gray-700'>
                      { t(CD.withheldMessage) }
                    </p>
                  </div>

                  <div className='flex flex-col sm:flex-row gap-4 justify-end'>
                    <button
                        onClick={() => {
                          updateCandidateOfferStatus(
                            selectedCandidate.jobApplicantId,
                            'Offer-Declined',
                          );
                        }}
                        disabled={isUpdatingStatus}
                        className='px-6 py-3 text-sm font-semibold rounded-2xl bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-md'
                    >
                      { t(CD.rejectCandidate) }
                    </button>
                    <button
                        onClick={() => {
                          updateCandidateOfferStatus(
                            selectedCandidate.jobApplicantId,
                            'Offer-Extended',
                          );
                        }}
                        disabled={isUpdatingStatus}
                        className='px-6 py-3 text-sm font-semibold rounded-2xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-md'
                    >
                      { t(CD.extendOffer) }
                    </button>
                  </div>
                </div>
              );
            }

            // Default buttons if no specific status panel
            return (
              <div className='flex justify-end space-x-3'>
                <Button
                    label={t(CD.acceptOffer)}
                    variant='primary'
                    dialogType={DIALOG_TYPE.SUCCESS}
                    disabled={
                      isUpdatingStatus ||
                    selectedCandidate.applicant_status === 'Offer-Accepted'
                    }
                    onClick={() =>
                      updateCandidateOfferStatus(
                        selectedCandidate.jobApplicantId,
                        'Offer-Accepted',
                      )
                    }
                />
                <Button
                    label={t(CD.rejectOffer)}
                    variant='secondary'
                    dialogType={DIALOG_TYPE.ERROR}
                    disabled={
                      isUpdatingStatus ||
                    selectedCandidate.applicant_status === 'Offer-Declined'
                    }
                    onClick={() =>
                      updateCandidateOfferStatus(
                        selectedCandidate.jobApplicantId,
                        'Offer-Declined',
                      )
                    }
                />
                <Button
                    label={t(CD.withholdOffer)}
                    variant='secondary'
                    dialogType={DIALOG_TYPE.WARNING}
                    disabled={
                      isUpdatingStatus ||
                    selectedCandidate.applicant_status === 'Offer-Withheld'
                    }
                    onClick={() =>
                      updateCandidateOfferStatus(
                        selectedCandidate.jobApplicantId,
                        'Offer-Withheld',
                      )
                    }
                />
              </div>
            );
          })() }
        </div>
      </div>
    </section>
  );
};
